import React from 'react';
import PropTypes from 'prop-types';
import { LuChartColumn, LuFileSpreadsheet, LuFlaskConical, LuFolderKanban, LuLoaderCircle, LuPlus, LuToggleLeft, LuToggleRight } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import PageHero from '../../../components/data/PageHero';
import StatTiles from '../../../components/data/StatTiles';
import { useTranslation } from '../../../i18n/LanguageContext';

// Phones: icon-only secondary actions beside a full-width "add"
const ICON_ONLY = 'max-sm:tw-w-10 max-sm:tw-px-0';

/** Title card of Proje Yönetimi with the page-level actions. */
export const ProjectPageHeader = ({ isMainAdmin, isExporting, isCreatingTest, onAdd, onAddTest, onOpenStats, onDownload }) => {
  const { t } = useTranslation();
  return (
    <PageHero
      icon={LuFolderKanban}
      gradient="tw-from-indigo-400 tw-to-violet-600 tw-shadow-violet-500/30"
      title={t('projectManagement.title')}
      subtitle={t('projectManagement.subtitle')}
      actionsClassName="tw-flex tw-items-center tw-gap-2 max-sm:[&>*:first-child]:tw-flex-1 sm:tw-flex-wrap"
    >
      <Button variant="primary" onClick={onAdd} className="tw-order-first sm:tw-order-last">
        <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
        {t('projectManagement.actions.add')}
      </Button>
      {isMainAdmin && (
        <Button onClick={onAddTest} disabled={isCreatingTest} aria-label={t('projectManagement.actions.addTest')} title={t('projectManagement.actions.addTest')} className={ICON_ONLY}>
          {isCreatingTest
            ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin tw-text-violet-600 dark:tw-text-violet-300" />
            : <LuFlaskConical aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-violet-600 dark:tw-text-violet-300" />}
          <span className="tw-hidden sm:tw-inline">{t('projectManagement.actions.addTest')}</span>
        </Button>
      )}
      <Button onClick={onOpenStats} aria-label={t('projectManagement.actions.stats')} title={t('projectManagement.actions.stats')} className={ICON_ONLY}>
        <LuChartColumn aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-blue-600 dark:tw-text-blue-300" />
        <span className="tw-hidden sm:tw-inline">{t('projectManagement.actions.stats')}</span>
      </Button>
      <Button
        onClick={onDownload}
        disabled={isExporting}
        aria-label={t('projectManagement.actions.download')}
        title={t('projectManagement.actions.downloadHint')}
        className={ICON_ONLY}
      >
        {isExporting
          ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin tw-text-emerald-600 dark:tw-text-emerald-400" />
          : <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />}
        <span className="tw-hidden sm:tw-inline">{t('projectManagement.actions.download')}</span>
      </Button>
    </PageHero>
  );
};

ProjectPageHeader.propTypes = {
  isMainAdmin: PropTypes.bool,
  isExporting: PropTypes.bool,
  isCreatingTest: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
  onAddTest: PropTypes.func.isRequired,
  onOpenStats: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
};

/** Total / active / inactive project counts. */
export const ProjectStats = ({ total, active, inactive, ready }) => {
  const { t } = useTranslation();
  const shareOf = (count) => (ready && total > 0 ? Math.min(1, count / total) : 0);
  const shareTitle = (count) => t('projectManagement.stats.share', { percent: Math.round(shareOf(count) * 100) });

  return (
    <StatTiles
      ready={ready}
      tiles={[
        {
          key: 'total',
          label: t('projectManagement.stats.total'),
          shortLabel: t('projectManagement.stats.totalShort'),
          value: total,
          icon: LuFolderKanban,
          gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30',
        },
        {
          key: 'active',
          label: t('projectManagement.stats.active'),
          shortLabel: t('projectManagement.stats.activeShort'),
          value: active,
          share: shareOf(active),
          shareTitle: shareTitle(active),
          icon: LuToggleRight,
          gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30',
          barClass: 'tw-bg-gradient-to-r tw-from-emerald-400 tw-to-teal-500',
        },
        {
          key: 'inactive',
          label: t('projectManagement.stats.inactive'),
          shortLabel: t('projectManagement.stats.inactiveShort'),
          value: inactive,
          share: shareOf(inactive),
          shareTitle: shareTitle(inactive),
          icon: LuToggleLeft,
          gradient: 'tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30',
          barClass: 'tw-bg-gradient-to-r tw-from-slate-300 tw-to-slate-500',
        },
      ]}
    />
  );
};

ProjectStats.propTypes = {
  total: PropTypes.number.isRequired,
  active: PropTypes.number.isRequired,
  inactive: PropTypes.number.isRequired,
  ready: PropTypes.bool,
};
